import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import BaseTable from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import * as PropTypes from 'prop-types';
import React from 'react';
import NonShoutyButton from './UI/NonShoutyButton';
import TableCollapsibleRow from './TableCollapsibleRow';
import TableFilterSelect from './TableFilterSelect';
import TablePagination from './TablePagination';
import TableSearch from './TableSearch';

const DEFAULT_SORT_EVENT = 'SORT';

const SortDirection = {
  ASC: 'ASC',
  DESC: 'DESC',
};

function TableCollapsible({
  tableProps,
  editableColumnChildren,
  activeRow,
  sortEvent,
  goToPageEvent,
  currentFilter,
  search: showSearch,
  emptyStateTitle,
  emptyStateButtonText,
  emptyStateButtonEvent,
}) {
  const { table, page, numPages } = tableProps || {};
  const { headers = [], rows = [], filters = [], customizedWidths = [] } =
    table || {};

  const showEmptyState = emptyStateTitle && !rows.length;
  const search = currentFilter && currentFilter.search;
  const showFilters = Array.isArray(filters) && filters.length > 0;

  return (
    <Box>
      {(showFilters || showSearch) && (
        <Box my={2} display="flex" width="100%">
          <Box flex="1" m={0.5} textAlign="left">
            <TableSearch search={search} />
          </Box>
          {showFilters && (
            <Box display="flex" justifyContent="flex-end">
              {(filters || []).map((filter, index) => (
                <TableFilterSelect
                  key={index}
                  filter={filter}
                  currentFilter={currentFilter}
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      <Paper variant="outlined">
        <TableContainer>
          <BaseTable
            size="medium"
            style={{
              tableLayout: 'fixed',
            }}
          >
            <colgroup>
              {headers.map((column, cellIndex) => {
                return (
                  <col
                    style={{ width: customizedWidths[cellIndex] }}
                    span={1}
                  />
                );
              })}
            </colgroup>
            <TableHead>
              <TableRow>
                {headers.map((header, headerIndex) => {
                  const { name, sort, direction } = header;
                  const onClick = () => {};

                  return sort ? (
                    <TableCell key={headerIndex}>
                      <TableSortLabel
                        active={direction !== null}
                        direction={
                          direction === SortDirection.ASC || direction === null
                            ? 'asc'
                            : 'desc'
                        }
                        onClick={onClick}
                      >
                        {name}
                      </TableSortLabel>
                    </TableCell>
                  ) : (
                    <TableCell key={headerIndex}>{name}</TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {showEmptyState ? (
                <TableCell colSpan={headers.length}>
                  <Box mt={2} mb={1} textAlign="center">
                    <Typography variant="subtitle2">
                      {emptyStateTitle}
                    </Typography>
                    {emptyStateButtonText ? (
                      <NonShoutyButton
                        color="secondary"
                        onClick={emptyStateButtonEvent}
                        startIcon={<AddIcon />}
                      >
                        {emptyStateButtonText}
                      </NonShoutyButton>
                    ) : (
                      ''
                    )}
                  </Box>
                </TableCell>
              ) : (
                Array.isArray(rows) &&
                rows.map((row, rowIndex) => {
                  const { columns: rowColumns = [], childRows = [] } = row;
                  return (
                    <TableCollapsibleRow
                      columns={rowColumns}
                      rowIndex={rowIndex}
                      editableColumnChildren={editableColumnChildren}
                      activeRow={activeRow}
                      childRows={childRows}
                      {...tableProps}
                    />
                  );
                })
              )}
            </TableBody>
          </BaseTable>
        </TableContainer>
        {!showEmptyState && numPages > 1 && (
          <TablePagination
            numberOfPages={numPages}
            currentPage={page}
            goToPageEvent={goToPageEvent}
          />
        )}
      </Paper>
    </Box>
  );
}

TableCollapsible.defaultProps = {
  editableColumnChildren: {},
  activeRow: undefined,
  sortEvent: DEFAULT_SORT_EVENT,
  goToPageEvent: undefined,
  currentFilter: {},
  search: false,
  emptyStateTitle: undefined,
  emptyStateButtonText: undefined,
  emptyStateButtonEvent: undefined,
};

TableCollapsible.propTypes = {
  tableProps: PropTypes.shape({
    filter: PropTypes.shape({
      search: PropTypes.string,
      locale: PropTypes.string,
      status: PropTypes.string,
    }),
    ordering: PropTypes.shape({
      sort: PropTypes.string,
      direction: PropTypes.oneOf([SortDirection.ASC, SortDirection.DESC]),
    }),
    page: PropTypes.number.isRequired,
    countPerPage: PropTypes.number.isRequired,
    numPages: PropTypes.number.isRequired,
    table: PropTypes.shape({
      headers: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          sort: PropTypes.string,
          direction: PropTypes.string,
        }).isRequired,
      ).isRequired,
      rows: PropTypes.arrayOf(
        PropTypes.shape({
          columns: PropTypes.arrayOf(PropTypes.any).isRequired,
          childRows: PropTypes.arrayOf(PropTypes.any).isRequired,
          link: PropTypes.string.isRequired,
        }).isRequired,
      ).isRequired,
      filters: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          name: PropTypes.string,
          values: PropTypes.arrayOf(
            PropTypes.shape({
              name: PropTypes.string,
              value: PropTypes.string,
              selected: PropTypes.bool,
            }).isRequired,
          ),
        }).isRequired,
      ),
      customizedWidths: PropTypes.arrayOf(PropTypes.array).isRequired,
      iconColumnIndex: PropTypes.number,
    }).isRequired,
  }).isRequired,
  editableColumnChildren: PropTypes.objectOf(PropTypes.any),
  activeRow: PropTypes.number,
  sortEvent: PropTypes.string,
  goToPageEvent: PropTypes.string,
  currentFilter: PropTypes.objectOf(PropTypes.any),
  search: PropTypes.bool,
  emptyStateTitle: PropTypes.string,
  emptyStateButtonText: PropTypes.string,
  emptyStateButtonEvent: PropTypes.func,
};

export default TableCollapsible;
